// @ts-check
import React from 'react';
import PropTypes from 'prop-types';

import { HiX } from 'react-icons/hi';

import { TestableButton, TestableText } from '@components/general/TestableComponents';
import { listActiveFilters, removeValue } from './filterUtils';

/**
 * The row of chips showing what is currently filtered, each removable on its own, plus a
 * link that clears the lot. Renders nothing when the selection is empty, rather than an
 * empty row leaving a gap under the toolbar.
 *
 * @param {Object} props
 * @param {import('./filterUtils').FilterGroup[]} props.groups
 * @param {import('./filterUtils').FilterSelection} props.selection
 * @param {(selection: import('./filterUtils').FilterSelection) => void} props.onChange
 * @param {string} [props.dataTestId]
 * @returns {JSX.Element|null}
 */
const ActiveFilterChips = ({ groups = [], selection = {}, onChange, dataTestId = 'active-filters' }) => {
  const active = listActiveFilters(groups, selection);

  if (active.length === 0) return null;

  return (
    <div data-testid={dataTestId} className="flex flex-wrap items-center gap-2">
      <TestableText
        dataTestId={`${dataTestId}-label`}
        className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300"
      >
        Active Filters
      </TestableText>

      {active.map((filter) => (
        <span
          key={`${filter.groupKey}:${filter.value}`}
          data-testid={`${dataTestId}-chip-${filter.groupKey}-${filter.value}`}
          className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 py-1 pl-3 pr-1 text-xs text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
        >
          {filter.label}
          {/*
            A button, not a clickable icon: the only way to remove one filter, so it has
            to be keyboard-operable. The label names the filter - a row of identical
            "Remove" buttons tells a screen reader user nothing.
          */}
          <button
            type="button"
            data-testid={`${dataTestId}-remove-${filter.groupKey}-${filter.value}`}
            aria-label={`Remove filter ${filter.label}`}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-blue-100 dark:hover:bg-blue-900"
            onClick={() => onChange?.(removeValue(selection, filter.groupKey, filter.value))}
          >
            <HiX className="h-3 w-3" />
          </button>
        </span>
      ))}

      <TestableButton
        dataTestId={`${dataTestId}-clear-all`}
        variant="text"
        className="p-1 text-xs normal-case text-blue-500"
        onClick={() => onChange?.({})}
      >
        Clear all
      </TestableButton>
    </div>
  );
};

ActiveFilterChips.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      options: PropTypes.array.isRequired,
      chipPrefix: PropTypes.string,
    })
  ),
  selection: PropTypes.object,
  onChange: PropTypes.func,
  dataTestId: PropTypes.string,
};

export default ActiveFilterChips;